import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
    ShieldCheck,
    LogOut,
    LifeBuoy,
    ListTodo,
    Users,
    LayoutDashboard,
    Package,
    Boxes,
    LayoutTemplate,
    UserCog,
    ScrollText,
    Cog,
    Plug,
    Percent,
    Search,
    Radio,
    CreditCard,
    Sparkles,
    Rocket,
    BarChart3,
    FileText,
    CheckCircle2,
    Zap,
    AlertCircle,
    Megaphone,
    TrendingUp,
    Menu,
    X,
} from "lucide-react";
import { clearSession, getUser } from "@/lib/api";
import NotificationsBell from "@/components/NotificationsBell";

const adminLinks = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
    { to: "/admin/operations", label: "Operations Center", icon: ListTodo, testid: "admin-nav-operations" },
    { to: "/admin/queue", label: "Approval queue", icon: CheckCircle2, testid: "admin-nav-queue" },
    { to: "/admin/doctors", label: "Doctors", icon: Users, testid: "admin-nav-doctors" },
    { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "admin-nav-payments" },
    { to: "/admin/ai-jobs", label: "AI Jobs", icon: Sparkles, testid: "admin-nav-ai-jobs" },
    { to: "/admin/publishing", label: "Publishing", icon: Rocket, testid: "admin-nav-publishing" },
    { to: "/admin/leads", label: "Leads", icon: BarChart3, testid: "admin-nav-leads" },
    { to: "/admin/crm", label: "CRM", icon: Users, testid: "admin-nav-crm" },
    { to: "/admin/support", label: "Support tickets", icon: LifeBuoy, testid: "admin-nav-support" },
    { to: "/admin/activity", label: "Activity", icon: FileText, testid: "admin-nav-activity" },
];

const superLinks = [
    { to: "/super-admin", label: "Overview", icon: LayoutDashboard, testid: "super-nav-overview" },
    { to: "/super-admin/packages", label: "Packages", icon: Package, testid: "super-nav-packages" },
    { to: "/super-admin/features", label: "Features", icon: Boxes, testid: "super-nav-features" },
    { to: "/super-admin/templates", label: "Templates", icon: LayoutTemplate, testid: "super-nav-templates" },
    { to: "/super-admin/admins", label: "Admin users", icon: UserCog, testid: "super-nav-admins" },
    { to: "/super-admin/audit", label: "Audit logs", icon: ScrollText, testid: "super-nav-audit" },
    { to: "/super-admin/integrations", label: "Integrations", icon: Plug, testid: "super-nav-integrations" },
    { to: "/super-admin/communications", label: "Communications", icon: Radio, testid: "super-nav-communications" },
    { to: "/super-admin/crm", label: "CRM", icon: Users, testid: "super-nav-crm" },
    { to: "/super-admin/dashboards", label: "Ops dashboards", icon: BarChart3, testid: "super-nav-dashboards" },
    { to: "/super-admin/automation", label: "Automation rules", icon: Zap, testid: "super-nav-automation" },
    { to: "/super-admin/maintenance-mode", label: "Maintenance mode", icon: AlertCircle, testid: "super-nav-maintenance-mode" },
    { to: "/super-admin/business", label: "Business", icon: Percent, testid: "super-nav-business" },
    { to: "/super-admin/marketing", label: "Marketing & Ads", icon: Megaphone, testid: "super-nav-marketing" },
    { to: "/super-admin/marketing-pages", label: "Marketing pages", icon: FileText, testid: "super-nav-marketing-pages" },
    { to: "/super-admin/analytics", label: "Website analytics", icon: TrendingUp, testid: "super-nav-analytics" },
    { to: "/super-admin/search-rankings", label: "Search rankings", icon: Search, testid: "super-nav-rankings" },
    { to: "/super-admin/settings", label: "System settings", icon: Cog, testid: "super-nav-settings" },
];

export default function AdminShell({ children }) {
    const user = getUser();
    const nav = useNavigate();
    const isSuper = user?.role === "super_admin";
    const [sidebarOpen, setSidebarOpen] = useState(false);

    return (
        <div className="min-h-screen bg-[#F5F7FB] flex" data-testid="admin-shell">
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="admin-sidebar-backdrop" />
            )}
            <aside className={"fixed lg:static inset-y-0 left-0 w-72 bg-[#0F1B2D] text-white flex flex-col z-50 transform transition-transform lg:translate-x-0 " + (sidebarOpen ? "translate-x-0" : "-translate-x-full")}>
                <div className="px-5 pt-6 pb-5 flex items-center gap-2.5">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 rounded-lg object-contain bg-white p-0.5 shadow-lg shadow-[#2563EB]/30" data-testid="admin-shell-brand-icon" />
                    <div className="leading-tight flex-1 min-w-0">
                        <div className="text-[15px] font-bold tracking-tight">DOCTOR DIGITAL ID</div>
                        <div className="text-[10px] uppercase tracking-widest text-white/45">
                            {isSuper ? "Super Admin" : "Admin Console"}
                        </div>
                    </div>
                    <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/70 hover:text-white p-1" data-testid="admin-sidebar-close" aria-label="Close menu">
                        <X size={18} />
                    </button>
                </div>
                <div className="px-5 pb-4">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-[12px] text-white/70">
                        Role: <span className="text-white font-semibold capitalize">{user?.role?.replaceAll("_", " ")}</span>
                    </div>
                </div>
                <nav className="px-3 flex-1 space-y-4 overflow-y-auto pb-4">
                    <div>
                        <div className="px-3 pb-1.5 text-[10px] uppercase tracking-widest text-white/35 font-semibold">Operations</div>
                        <div className="space-y-0.5">
                            {adminLinks.map((it) => {
                                const Icon = it.icon;
                                return (
                                    <NavLink key={it.to} to={it.to} end onClick={() => setSidebarOpen(false)} className={({ isActive }) => "ddi-nav-item" + (isActive ? " active" : "")} data-testid={it.testid}>
                                        <Icon size={16} strokeWidth={2.1} />
                                        <span className="flex-1">{it.label}</span>
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                    {isSuper && (
                        <div>
                            <div className="px-3 pb-1.5 text-[10px] uppercase tracking-widest text-white/35 font-semibold">Platform</div>
                            <div className="space-y-0.5">
                                {superLinks.map((it) => {
                                    const Icon = it.icon;
                                    return (
                                        <NavLink key={it.to} to={it.to} end onClick={() => setSidebarOpen(false)} className={({ isActive }) => "ddi-nav-item" + (isActive ? " active" : "")} data-testid={it.testid}>
                                            <Icon size={16} strokeWidth={2.1} />
                                            <span className="flex-1">{it.label}</span>
                                        </NavLink>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </nav>
                <div className="px-4 py-4 border-t border-white/8">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#3A80F7] to-[#2456B5] flex items-center justify-center text-[13px] font-bold">
                            {(user?.full_name || "A").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold truncate">{user?.full_name}</div>
                            <div className="text-[11px] text-white/50 truncate">{user?.email}</div>
                        </div>
                        <button onClick={() => { clearSession(); nav("/login"); }} className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10" data-testid="admin-logout" title="Log out">
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </aside>
            <main className="flex-1 min-w-0 w-full">
                <header className="h-16 border-b border-[#E5E9F2] bg-white flex items-center justify-between px-4 md:px-6 lg:px-8 sticky top-0 z-30 gap-3">
                    <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[#0F172A] p-1.5 -ml-1.5 rounded-md hover:bg-[#F5F7FB]" data-testid="admin-sidebar-open" aria-label="Open menu">
                        <Menu size={20} />
                    </button>
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold hidden sm:block">
                            {isSuper ? "Super admin" : "Admin"}
                        </div>
                        <div className="text-[14px] md:text-[15px] font-semibold text-[#0F172A] truncate">{user?.full_name}</div>
                    </div>
                    <NotificationsBell />
                </header>
                <div className="p-4 md:p-6 lg:p-8 max-w-[1400px]">{children}</div>
            </main>
        </div>
    );
}
